/**
 * NMS Buybox Accordion Component
 * Extends NMSBaseClass following NMS Component Instance Architecture
 * Generic accordion functionality for buybox components (discounts, FAQs, offers, etc.)
 */

class NMSBuyboxAccordion extends window.NMSBaseClass {
  #isInitialized = false;
  #eventListeners = [];
  #isDescriptionExpanded = false;

  constructor(element) {
    super();
    this.element = element; // REQUIRED: Store element reference
    this.accordionId = element.dataset.accordionId || `accordion-${Date.now()}`; // REQUIRED: Unique identifier
    this.accordionType = element.dataset.accordionType || 'generic';

    // REQUIRED: Store in global registry for external access
    window.neemans.buyboxAccordion = window.neemans.buyboxAccordion || {};
    window.neemans.buyboxAccordion[this.accordionId] = this;
  }

  init() {
    if (this.#isInitialized) return;

    super.init();
    this.#setupAccordionRefs();
    this.#initializeAccordionState();
    this.#bindAccordionEvents();

    // Set initial state - expanded by default
    this.#setInitialState();

    // Initialize description-specific functionality
    if (this.accordionType === 'description') {
      this.#initDescriptionExpansion();
    }

    this.#isInitialized = true;
  }

  // REQUIRED: Scope all setup to this.element
  #setupAccordionRefs() {
    // Query only within component scope using data attributes
    this.refs.toggleButton = this.element.querySelector('[data-nms-js="buybox-accordion-toggle"]');
    this.refs.accordionBody = this.element.querySelector('[data-nms-js="buybox-accordion-body"]');
    this.refs.toggleIcon = this.refs.toggleButton?.querySelector(
      '.nms-product-discounts__toggle, .nms-product-description__toggle'
    );

    // Description-specific elements
    this.refs.descriptionText = this.element.querySelector('[data-nms-js="description-text"]');
    this.refs.expandButton = this.element.querySelector('[data-nms-js="description-expand-button"]');
    this.refs.expandButtonContainer = this.element.querySelector('.nms-product-description__expand-button-container');
  }

  #initializeAccordionState() {
    // Set up global data in window.neemans namespace
    this.setGlobalData(`buyboxAccordion.${this.accordionId}`, {
      isExpanded: false, // Default to collapsed
      accordionType: this.accordionType,
      isDescriptionExpanded: false,
    });
  }

  #setInitialState() {
    if (!this.refs.toggleButton || !this.refs.accordionBody) return;

    // Check if accordion should be expanded by default
    const defaultExpanded = this.element.dataset.defaultExpanded === 'true';

    if (defaultExpanded) {
      // Set initial state - expanded
      this.refs.accordionBody.classList.remove('collapsed');
      this.refs.accordionBody.classList.add('expanded');

      if (this.refs.toggleIcon) {
        this.refs.toggleIcon.classList.remove('rotated');
      }
      this.refs.toggleButton.setAttribute('aria-expanded', 'true');

      // Update global state
      this.setGlobalData(`buyboxAccordion.${this.accordionId}.isExpanded`, true);
    } else {
      // Set initial state - collapsed by default (HTML already has collapsed class)
      // Ensure expanded class is removed if present
      this.refs.accordionBody.classList.remove('expanded');
      this.refs.accordionBody.classList.add('collapsed');

      if (this.refs.toggleIcon) {
        this.refs.toggleIcon.classList.add('rotated');
      }
      this.refs.toggleButton.setAttribute('aria-expanded', 'false');
    }
  }

  #bindAccordionEvents() {
    if (!this.refs.toggleButton) return;

    // Toggle button click
    const toggleHandler = () => this.#toggle();
    this.refs.toggleButton.addEventListener('click', toggleHandler);
    this.#eventListeners.push({ element: this.refs.toggleButton, event: 'click', handler: toggleHandler });

    // Add keyboard support
    const keydownHandler = e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.#toggle();
      }
    };
    this.refs.toggleButton.addEventListener('keydown', keydownHandler);
    this.#eventListeners.push({ element: this.refs.toggleButton, event: 'keydown', handler: keydownHandler });
  }

  #initDescriptionExpansion() {
    if (!this.refs.descriptionText || !this.refs.expandButton) return;

    // Set initial truncated state
    this.refs.descriptionText.classList.add('truncated');

    // Bind expand button event
    const expandHandler = () => this.#toggleDescriptionExpansion();
    this.refs.expandButton.addEventListener('click', expandHandler);
    this.#eventListeners.push({ element: this.refs.expandButton, event: 'click', handler: expandHandler });

    // Add keyboard support for expand button
    const expandKeydownHandler = e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.#toggleDescriptionExpansion();
      }
    };
    this.refs.expandButton.addEventListener('keydown', expandKeydownHandler);
    this.#eventListeners.push({ element: this.refs.expandButton, event: 'keydown', handler: expandKeydownHandler });
  }

  #toggleDescriptionExpansion() {
    if (!this.refs.descriptionText || !this.refs.expandButton) return;

    // Since we only want to expand once and then hide the button, we only handle the expand case
    if (!this.#isDescriptionExpanded) {
      // Expand to full content
      this.refs.descriptionText.classList.remove('truncated');
      this.#isDescriptionExpanded = true;

      // Update global state
      this.setGlobalData(`buyboxAccordion.${this.accordionId}.isDescriptionExpanded`, true);

      // Hide the button and its container
      if (this.refs.expandButtonContainer) {
        this.refs.expandButtonContainer.style.display = 'none';
      }

      // Dispatch NMS event
      this.dispatchNMSEvent('buybox-accordion:description-expand', {
        accordionId: this.accordionId,
        accordionType: this.accordionType,
        component: this,
      });
    }
  }

  #toggle() {
    const isExpanded = this.refs.accordionBody.classList.contains('expanded');

    if (isExpanded) {
      this.#collapse();
    } else {
      this.#expand();
    }
  }

  #expand() {
    this.refs.accordionBody.classList.remove('collapsed');
    this.refs.accordionBody.classList.add('expanded');
    if (this.refs.toggleIcon) {
      this.refs.toggleIcon.classList.remove('rotated');
    }
    this.refs.toggleButton.setAttribute('aria-expanded', 'true');

    // Update global state
    this.setGlobalData(`buyboxAccordion.${this.accordionId}.isExpanded`, true);

    // Dispatch NMS event for external listeners
    this.dispatchNMSEvent('buybox-accordion:expand', {
      accordionId: this.accordionId,
      accordionType: this.accordionType,
      component: this,
    });
  }

  #collapse() {
    this.refs.accordionBody.classList.remove('expanded');
    this.refs.accordionBody.classList.add('collapsed');
    if (this.refs.toggleIcon) {
      this.refs.toggleIcon.classList.add('rotated');
    }
    this.refs.toggleButton.setAttribute('aria-expanded', 'false');

    // Update global state
    this.setGlobalData(`buyboxAccordion.${this.accordionId}.isExpanded`, false);

    // Dispatch NMS event for external listeners
    this.dispatchNMSEvent('buybox-accordion:collapse', {
      accordionId: this.accordionId,
      accordionType: this.accordionType,
      component: this,
    });
  }

  // Public API methods
  expand() {
    this.#expand();
  }

  collapse() {
    this.#collapse();
  }

  toggle() {
    this.#toggle();
  }

  isExpanded() {
    return this.refs.accordionBody?.classList.contains('expanded') || false;
  }

  getAccordionType() {
    return this.accordionType;
  }

  getAccordionId() {
    return this.accordionId;
  }

  isDescriptionExpanded() {
    return this.#isDescriptionExpanded;
  }

  destroy() {
    // Clean up event listeners
    for (const listener of this.#eventListeners) {
      listener.element.removeEventListener(listener.event, listener.handler);
    }
    this.#eventListeners = [];

    // Clean up global reference
    if (window.neemans.buyboxAccordion?.[this.accordionId]) {
      delete window.neemans.buyboxAccordion[this.accordionId];
    }

    super.destroy();
    this.#isInitialized = false;
  }
}

// REQUIRED: Standard initialization pattern
function initNMSBuyboxAccordion(container = document) {
  if (!window.NMSBaseClass) {
    console.error('NMSBuyboxAccordion: NMSBaseClass required');
    return;
  }

  // Find all buybox accordion components
  const elements = container.querySelectorAll('[data-nms-js="buybox-accordion"]');

  for (const element of elements) {
    if (!element.hasAttribute('data-initialized')) {
      const instance = new NMSBuyboxAccordion(element);
      instance.init();
      element.setAttribute('data-initialized', 'true');
    }
  }
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNMSBuyboxAccordion);
} else {
  initNMSBuyboxAccordion();
}

// Handle theme editor reloads
document.addEventListener('shopify:section:load', event => {
  if (event.target.querySelector('[data-nms-js="buybox-accordion"]')) {
    initNMSBuyboxAccordion(event.target);
  }
});

// Listen for color content updates and re-initialize within the updated container
document.addEventListener('nms:color-content-updated', event => {
  const { container, source } = event.detail;
  initNMSBuyboxAccordion(container);
});

// Make available in global namespace
window.neemans = window.neemans || {};
window.neemans.NMSBuyboxAccordion = NMSBuyboxAccordion;

// Global initialization function for other components to use
window.initNMSBuyboxAccordion = initNMSBuyboxAccordion;
